import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, ArrowRight, Phone } from "lucide-react";
import { SERVICE_OPTIONS, PHONE, PHONE_HREF } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  full_name: "",
  phone: "",
  email: "",
  service_needed: "",
  travel_date: "",
  destination_country: "",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-[#0a1128] placeholder:text-slate-400 focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20 outline-none transition-[border-color,box-shadow]";

export const LeadForm = ({ sourcePage = "home", compact = false }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service_needed) {
      toast.error("Please fill in your name, phone, email, and the service you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source_page: sourcePage });
      setDone(true);
      toast.success("Request received! Our specialists will contact you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE + ".");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-8 md:p-10 text-center" data-testid="lead-form-success">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#d90429]/10 flex items-center justify-center mb-5">
          <ShieldCheck className="w-7 h-7 text-[#d90429]" />
        </div>
        <h3 className="font-display font-bold text-2xl text-[#0b192c]">Request received.</h3>
        <p className="text-slate-600 mt-3">A UPV specialist will reach out shortly. Traveling urgently?</p>
        <a href={PHONE_HREF} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3 font-semibold hover:bg-[#ef233c] transition-colors">
          <Phone className="w-4 h-4" /> Call {PHONE} now
        </a>
      </div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-6 md:p-8"
      data-testid="lead-form"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {!compact && (
        <div className="mb-6">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">Get a Free Quote</span>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] mt-2">Urgent Passport & Visa Quote</h3>
          <p className="text-slate-600 mt-2 text-[15px]">Need help before your trip? Our experts respond quickly.</p>
        </div>
      )}

      <a href={PHONE_HREF} className="mb-5 flex items-center justify-center gap-2 rounded-xl bg-[#0b192c] text-white px-4 py-3.5 font-semibold hover:bg-[#1a2a40] transition-colors" data-testid="lead-call-quote">
        <Phone className="w-4 h-4 text-[#ef233c]" /> Call for a quote: {PHONE}
      </a>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Full Name *</label>
          <input className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Traveler" data-testid="lead-name" />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Phone Number *</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(202) 555-0123" data-testid="lead-phone" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Email *</label>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="lead-email" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Service Needed *</label>
          <select className={inputCls} value={form.service_needed} onChange={set("service_needed")} data-testid="lead-service">
            <option value="">Select a service…</option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Travel Date</label>
          <input type="date" className={inputCls} value={form.travel_date} onChange={set("travel_date")} data-testid="lead-date" />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Destination Country</label>
          <input className={inputCls} value={form.destination_country} onChange={set("destination_country")} placeholder="e.g. France" data-testid="lead-destination" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-[#0b192c] mb-1.5">Message</label>
          <textarea rows={compact ? 2 : 3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your situation and timeline…" data-testid="lead-message" />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-4 font-semibold text-base hover:bg-[#ef233c] transition-colors disabled:opacity-60"
        data-testid="lead-submit"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Get Started Today <ArrowRight className="w-5 h-5" /></>}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
        <ShieldCheck className="w-3.5 h-3.5 text-[#0b192c]" /> Secure & encrypted. Your documents stay confidential.
      </p>
    </motion.form>
  );
};
